import { useState, useEffect, useCallback } from 'react';
import { adminAPI, collectionPointsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '@/components/ui/command';
import { Loader2, Download, Package, CheckCircle2, PackageCheck, MapPin, Building2, ShoppingBag, ChevronsUpDown, Check, X, AlertTriangle, Truck } from 'lucide-react';

const STAGE_LABELS = { packing: 'Packing' };
const VIEWS = [
  { key: 'all', label: 'All' },
  { key: 'unpacked', label: 'Has unpacked items' },
  { key: 'before_po', label: 'Packed before PO' },
  { key: 'packed', label: 'Fully packed' },
  { key: 'ready', label: 'Ready, awaiting collection' },
];
const isReadyStage = (o) => o.status === 'ready_for_collection' || o.status === 'collection_notification_sent';
const matchesView = (o, view) => {
  if (view === 'ready') return isReadyStage(o);
  if (isReadyStage(o)) return false;
  if (view === 'unpacked') return !o.all_packed;
  if (view === 'before_po') return (o.packed_before_po || 0) > 0;
  if (view === 'packed') return o.all_packed;
  return true;
};

export default function AdminPacking() {
  const [suppliers, setSuppliers] = useState([]);
  const [points, setPoints] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]); // [] = all
  const [cpId, setCpId] = useState('all');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [packingKey, setPackingKey] = useState(null);
  const [readyKey, setReadyKey] = useState(null);
  const [view, setView] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const [s, cp] = await Promise.all([adminAPI.listSuppliers(), collectionPointsAPI.listAll()]);
        setSuppliers((s.data || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        setPoints(cp.data || []);
      } catch { /* filters stay on defaults */ }
    })();
  }, []);

  const buildParams = useCallback(() => {
    const params = {};
    if (selectedSuppliers.length) params.supplier_ids = selectedSuppliers.join(',');
    if (cpId && cpId !== 'all') params.collection_point_id = cpId;
    return params;
  }, [selectedSuppliers, cpId]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getPackingList(buildParams());
      setGroups(res.data?.groups || []);
    } catch {
      setGroups([]);
      toast.error('Could not load packing list');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const toggleSupplier = (id) => {
    setSelectedSuppliers((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const togglePacked = async (order, sg) => {
    const key = `${order.id}:${sg.supplier_id}`;
    setPackingKey(key);
    const next = !sg.packed;
    try {
      const res = await adminAPI.setOrderPacked(order.id, next, sg.supplier_id);
      const newStatus = res.data?.status || order.status;
      setGroups((prev) => prev.map((cp) => ({
        ...cp,
        customers: cp.customers.map((c) => ({
          ...c,
          orders: c.orders.map((o) => {
            if (o.id !== order.id) return o;
            const supplier_groups = o.supplier_groups.map((g) =>
              g.supplier_id === sg.supplier_id
                ? { ...g, packed: next, packed_before_po: next && !g.ordered, state: next ? 'packed' : (g.ordered ? 'ordered' : 'not_ordered') }
                : g);
            const packed_suppliers = supplier_groups.filter((g) => g.packed).length;
            const packed_before_po = supplier_groups.filter((g) => g.packed_before_po).length;
            return {
              ...o, supplier_groups, packed_suppliers, packed_before_po,
              all_packed: packed_suppliers === o.total_suppliers,
              status: newStatus,
              status_label: newStatus !== o.status ? (STAGE_LABELS[newStatus] || o.status_label) : o.status_label,
            };
          }),
        })),
      })));
      setCounts((c) => ({ ...c, packed: c.packed + (next ? 1 : -1) }));
    } catch {
      toast.error('Could not update packed status');
    } finally {
      setPackingKey(null);
    }
  };

  const markReady = async (o) => {
    const outstanding = (o.total_suppliers || 0) - (o.packed_suppliers || 0);
    const beforePo = o.packed_before_po || 0;
    const warnings = [];
    if (outstanding > 0) warnings.push(`• ${outstanding} of ${o.total_suppliers} supplier portion(s) are not packed yet.`);
    if (beforePo > 0) warnings.push(`• ${beforePo} supplier portion(s) were packed before their PO was sent — remember to order & pay the supplier.`);
    if (warnings.length && !window.confirm(
      `${o.order_number}:\n${warnings.join('\n')}\n\nMark the WHOLE order Ready for Collection anyway?`
    )) return;
    setReadyKey(o.id);
    try {
      await adminAPI.updateOrder(o.id, { status: 'ready_for_collection' });
      toast.success(`${o.order_number} marked Ready for Collection`);
      fetchList();
    } catch {
      toast.error('Could not mark ready for collection');
    } finally {
      setReadyKey(null);
    }
  };

  const visibleGroups = groups
    .map((cp) => ({
      ...cp,
      customers: cp.customers
        .map((c) => ({ ...c, orders: c.orders.filter((o) => matchesView(o, view)) }))
        .filter((c) => c.orders.length),
    }))
    .filter((cp) => cp.customers.length);
  const visibleOrders = visibleGroups.flatMap((cp) => cp.customers.flatMap((c) => c.orders));
  const shown = {
    packed: visibleOrders.reduce((n, o) => n + (o.packed_suppliers || 0), 0),
    total: visibleOrders.reduce((n, o) => n + (o.total_suppliers || 0), 0),
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await adminAPI.exportPackingList({ ...buildParams(), view });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `AVZ_Packing_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Packing list exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="admin-packing">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-light text-foreground tracking-tight">Packing List</h1>
          <p className="font-body text-sm text-foreground/60 mt-1">
            Collection Point → Customer → Order → Supplier · <span data-testid="packing-count">{shown.packed}/{shown.total} packed</span>
          </p>
        </div>
        <Button
          onClick={exportXlsx}
          disabled={exporting || visibleOrders.length === 0}
          data-testid="export-packing-btn"
          className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm font-bold gap-2"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" strokeWidth={2.5} />}
          Export to Excel
        </Button>
      </div>

      {/* Vendor multi-select (searchable dropdown with tick boxes) */}
      <div className="mb-4">
        <p className="font-body text-xs uppercase tracking-wider text-foreground/50 mb-2">Vendors</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                data-testid="vendor-select-trigger"
                className="w-full sm:w-80 justify-between rounded-xl font-body font-normal bg-muted border-border"
              >
                <span className="truncate">
                  {selectedSuppliers.length === 0
                    ? 'All vendors'
                    : selectedSuppliers.length === 1
                      ? (suppliers.find((s) => s.id === selectedSuppliers[0])?.name || '1 vendor')
                      : `${selectedSuppliers.length} vendors selected`}
                </span>
                <ChevronsUpDown className="h-4 w-4 opacity-50 flex-shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-xl" align="start">
              <Command>
                <CommandInput placeholder="Search vendors..." data-testid="vendor-search-input" className="font-body" />
                <CommandList>
                  <CommandEmpty className="font-body text-sm py-4 text-center text-foreground/50">No vendors found.</CommandEmpty>
                  <CommandItem
                    value="All vendors"
                    onSelect={() => setSelectedSuppliers([])}
                    data-testid="vendor-option-all"
                    className="font-body cursor-pointer"
                  >
                    <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded border ${selectedSuppliers.length === 0 ? 'bg-teal border-teal' : 'border-border'}`}>
                      {selectedSuppliers.length === 0 && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                    </div>
                    All vendors
                  </CommandItem>
                  {suppliers.map((s) => {
                    const checked = selectedSuppliers.includes(s.id);
                    return (
                      <CommandItem
                        key={s.id}
                        value={s.name}
                        onSelect={() => toggleSupplier(s.id)}
                        data-testid={`vendor-option-${s.id}`}
                        className="font-body cursor-pointer"
                      >
                        <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded border ${checked ? 'bg-teal border-teal' : 'border-border'}`}>
                          {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                        </div>
                        {s.name}
                      </CommandItem>
                    );
                  })}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {selectedSuppliers.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="vendor-clear-btn"
              onClick={() => setSelectedSuppliers([])}
              className="rounded-full text-xs text-foreground/60 hover:text-foreground gap-1"
            >
              <X className="h-3.5 w-3.5" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Collection point filter + packing view */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
        <Select value={cpId} onValueChange={setCpId}>
          <SelectTrigger className="w-full sm:w-64 rounded-xl font-body" data-testid="packing-cp-filter">
            <SelectValue placeholder="Collection point" />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="all" className="font-body">All collection points</SelectItem>
            {points.map((p) => (
              <SelectItem key={p.id} value={p.id} className="font-body">{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="packing-view-filter">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => setView(v.key)}
              data-testid={`packing-view-${v.key}`}
              className={`h-8 px-3 rounded-full border font-body text-xs transition-colors ${view === v.key ? 'bg-teal text-white border-teal' : 'bg-transparent border-border text-foreground/70 hover:bg-muted'}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
      ) : visibleGroups.length === 0 ? (
        <div className="text-center py-20" data-testid="packing-empty">
          <Package className="h-10 w-10 text-foreground/30 mx-auto mb-3" />
          <p className="font-body text-foreground/60">No paid orders to pack for this selection.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {visibleGroups.map((cp) => (
            <div key={cp.collection_point_name} className="rounded-2xl overflow-hidden border border-border" data-testid="packing-cp-group">
              {/* Collection point band */}
              <div className="bg-teal px-5 py-3 flex items-center gap-2.5">
                <MapPin className="h-5 w-5 text-white" strokeWidth={2} />
                <span className="font-heading text-lg text-white">{cp.collection_point_name}</span>
              </div>

              <div className="bg-card divide-y divide-border">
                {cp.customers.map((cust) => (
                  <div key={cust.name + cust.email} className="p-5" data-testid="packing-customer-group">
                    <div className="flex items-center gap-3 flex-wrap mb-3">
                      <Building2 className="h-4 w-4 text-gold" strokeWidth={2.2} />
                      <span className="font-heading text-base text-foreground">{cust.name}</span>
                      {cust.phone && <span className="font-body text-xs text-foreground/60">{cust.phone}</span>}
                    </div>

                    <div className="space-y-4">
                      {cust.orders.map((o) => {
                        const isReady = isReadyStage(o);
                        const pct = o.total_suppliers ? Math.round((o.packed_suppliers / o.total_suppliers) * 100) : 0;
                        return (
                        <div key={o.id} className="rounded-xl bg-muted/40 border border-border/60 p-3" data-testid={`packing-order-${o.id}`}>
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <ShoppingBag className="h-4 w-4 text-teal" />
                            <span className="font-body text-sm font-bold text-foreground">{o.order_number}</span>
                            <Badge className="bg-vintage/15 text-vintage border-vintage/30 rounded-full font-body text-[11px]">{o.status_label}</Badge>
                            <Badge className={`rounded-full font-body text-[11px] capitalize ${o.payment_status === 'paid' ? 'bg-teal/15 text-teal border-teal/30' : 'bg-orange-400/15 text-orange-400 border-orange-400/30'}`}>{o.payment_status}</Badge>
                          </div>

                          {/* Per-order packing progress + Ready for Collection */}
                          <div className="flex items-center gap-3 mb-3 flex-wrap" data-testid={`packing-progress-${o.id}`}>
                            <div className="flex items-center gap-2 flex-1 min-w-[160px]">
                              <div className="h-2 flex-1 rounded-full bg-border/70 overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${o.all_packed ? 'bg-teal' : 'bg-gold'}`} style={{ width: `${pct}%` }} />
                              </div>
                              <span className="font-body text-[11px] font-semibold text-foreground/70 whitespace-nowrap">
                                {o.packed_suppliers} of {o.total_suppliers} packed
                              </span>
                            </div>
                            {isReady ? (
                              <Badge className="bg-teal/15 text-teal border-teal/30 rounded-full font-body text-[11px] gap-1" data-testid={`packing-ready-badge-${o.id}`}>
                                <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} /> Ready for Collection
                              </Badge>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => markReady(o)}
                                disabled={readyKey === o.id}
                                data-testid={`packing-mark-ready-${o.id}`}
                                className={`h-7 rounded-full font-body text-[11px] gap-1.5 ${o.all_packed ? 'bg-teal text-white hover:bg-teal/90' : 'bg-transparent border border-gold/50 text-gold hover:bg-gold/10'}`}
                              >
                                {readyKey === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : o.all_packed ? <Truck className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                                Mark Ready for Collection
                              </Button>
                            )}
                          </div>

                          <div className="space-y-3">
                            {o.supplier_groups.map((sg) => {
                              const st = sg.state || (sg.packed ? 'packed' : 'not_ordered');
                              return (
                              <div key={sg.supplier_id} className={`rounded-lg border p-3 ${st === 'packed' ? 'bg-teal/5 border-teal/30' : 'bg-card border-border/60'}`} data-testid={`packing-supplier-${o.id}-${sg.supplier_id}`}>
                                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                                  <span className="font-body text-sm font-semibold text-foreground inline-flex items-center gap-1.5">
                                    <Package className="h-3.5 w-3.5 text-gold" /> {sg.supplier_name}
                                    {sg.ordered
                                      ? <Badge className="bg-gold/15 text-gold border-gold/30 rounded-full font-body text-[10px]" data-testid={`packing-po-state-${o.id}-${sg.supplier_id}`}>PO sent</Badge>
                                      : <Badge className="bg-foreground/10 text-foreground/50 border-foreground/20 rounded-full font-body text-[10px]" data-testid={`packing-po-state-${o.id}-${sg.supplier_id}`}>PO not sent</Badge>}
                                    {st === 'packed'
                                      ? <Badge className="bg-teal/15 text-teal border-teal/30 rounded-full font-body text-[10px] gap-1" data-testid={`packing-state-${o.id}-${sg.supplier_id}`}><CheckCircle2 className="h-3 w-3" strokeWidth={2.5} /> Packed</Badge>
                                      : <Badge className="bg-transparent text-foreground/60 border-border rounded-full font-body text-[10px]" data-testid={`packing-state-${o.id}-${sg.supplier_id}`}>To pack</Badge>}
                                    {sg.packed_before_po && <Badge className="bg-orange-400/15 text-orange-400 border-orange-400/30 rounded-full font-body text-[10px] gap-1" data-testid={`packing-before-po-${o.id}-${sg.supplier_id}`}><AlertTriangle className="h-3 w-3" /> Packed before PO</Badge>}
                                  </span>
                                  <label className="flex items-center gap-2 cursor-pointer select-none" data-testid={`packing-toggle-${o.id}-${sg.supplier_id}`}>
                                    {packingKey === `${o.id}:${sg.supplier_id}`
                                      ? <Loader2 className="h-4 w-4 animate-spin text-gold" />
                                      : <Checkbox checked={sg.packed} onCheckedChange={() => togglePacked(o, sg)} className="data-[state=checked]:bg-teal data-[state=checked]:border-teal" />}
                                    <span className="font-body text-xs text-foreground/70 inline-flex items-center gap-1"><PackageCheck className="h-3.5 w-3.5" /> {sg.packed ? 'Packed' : 'Mark packed'}</span>
                                  </label>
                                </div>
                                <table className="w-full table-fixed">
                                  <colgroup>
                                    <col />
                                    <col className="w-32" />
                                    <col className="w-20" />
                                  </colgroup>
                                  <thead>
                                    <tr>
                                      <th className="text-left font-body text-[11px] uppercase tracking-wider text-foreground/50 pb-1.5">Product</th>
                                      <th className="text-left font-body text-[11px] uppercase tracking-wider text-foreground/50 pb-1.5">Unit Size</th>
                                      <th className="text-right font-body text-[11px] uppercase tracking-wider text-foreground/50 pb-1.5">Qty</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {sg.items.map((it, idx) => (
                                      <tr key={idx} className="border-t border-border/50">
                                        <td className="font-body text-sm text-foreground py-1.5 pr-3 truncate">{it.product_name}</td>
                                        <td className="font-body text-sm text-foreground/70 py-1.5">{it.unit_size || '—'}</td>
                                        <td className="font-body text-sm text-foreground py-1.5 text-right font-bold">{it.quantity}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            );})}
                          </div>
                        </div>
                      );})}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
